/**
 * Pure PNG encode/decode codec — no platform dependencies.
 * Used by RawImage filters and the NodeImageCanvas implementation.
 */

function readPngChunks(data: Uint8Array): { type: string; data: Uint8Array }[] {
  const chunks: { type: string; data: Uint8Array }[] = [];
  let offset = 8; // Skip 8-byte PNG signature
  while (offset < data.length) {
    const length =
      (data[offset] << 24) | (data[offset + 1] << 16) | (data[offset + 2] << 8) | data[offset + 3];
    offset += 4;
    const type = String.fromCharCode(
      data[offset],
      data[offset + 1],
      data[offset + 2],
      data[offset + 3]
    );
    offset += 4;
    const chunkData = data.slice(offset, offset + length);
    offset += length + 4; // skip data + CRC
    chunks.push({ type, data: chunkData });
  }
  return chunks;
}

function readUint32BE(data: Uint8Array, offset: number): number {
  return (
    (data[offset] << 24) | (data[offset + 1] << 16) | (data[offset + 2] << 8) | data[offset + 3]
  );
}

function paethPredictor(a: number, b: number, c: number): number {
  const p = a + b - c;
  const pa = Math.abs(p - a);
  const pb = Math.abs(p - b);
  const pc = Math.abs(p - c);
  if (pa <= pb && pa <= pc) return a;
  if (pb <= pc) return b;
  return c;
}

function inflateSync(compressed: Uint8Array): Uint8Array {
  return zlibInflate(compressed);
}

function zlibInflate(data: Uint8Array): Uint8Array {
  return rawInflate(data, 2); // Skip 2-byte zlib header
}

function rawInflate(data: Uint8Array, startOffset: number): Uint8Array {
  const output: number[] = [];
  let offset = startOffset;
  let bitBuf = 0;
  let bitCount = 0;

  function readBits(n: number): number {
    while (bitCount < n) {
      if (offset >= data.length) throw new Error('Unexpected end of data');
      bitBuf |= data[offset++] << bitCount;
      bitCount += 8;
    }
    const val = bitBuf & ((1 << n) - 1);
    bitBuf >>= n;
    bitCount -= n;
    return val;
  }

  const lenBase = [
    3, 4, 5, 6, 7, 8, 9, 10, 11, 13, 15, 17, 19, 23, 27, 31, 35, 43, 51, 59, 67, 83, 99, 115, 131,
    163, 195, 227, 258,
  ];
  const lenExtra = [
    0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 2, 2, 2, 2, 3, 3, 3, 3, 4, 4, 4, 4, 5, 5, 5, 5, 0,
  ];
  const distBase = [
    1, 2, 3, 4, 5, 7, 9, 13, 17, 25, 33, 49, 65, 97, 129, 193, 257, 385, 513, 769, 1025, 1537, 2049,
    3073, 4097, 6145, 8193, 12289, 16385, 24577,
  ];
  const distExtra = [
    0, 0, 0, 0, 1, 1, 2, 2, 3, 3, 4, 4, 5, 5, 6, 6, 7, 7, 8, 8, 9, 9, 10, 10, 11, 11, 12, 12, 13,
    13,
  ];

  function buildHuffmanTable(lengths: number[]): { symbols: number[]; counts: number[] } {
    const maxLen = Math.max(...lengths);
    const counts = new Array(maxLen + 1).fill(0);
    for (const l of lengths) if (l > 0) counts[l]++;
    const offsets = new Array(maxLen + 1).fill(0);
    for (let i = 1; i <= maxLen; i++) offsets[i] = offsets[i - 1] + counts[i - 1];
    const symbols = new Array(lengths.length).fill(0);
    for (let i = 0; i < lengths.length; i++) {
      if (lengths[i] > 0) symbols[offsets[lengths[i]]++] = i;
    }
    return { symbols, counts };
  }

  function decodeSymbol(table: { symbols: number[]; counts: number[] }): number {
    let code = 0,
      first = 0,
      index = 0;
    for (let len = 1; len < table.counts.length; len++) {
      code |= readBits(1);
      const count = table.counts[len];
      if (code < first + count) return table.symbols[index + code - first];
      index += count;
      first = (first + count) << 1;
      code <<= 1;
    }
    throw new Error('Invalid Huffman code');
  }

  function buildFixedLitLenTable(): { symbols: number[]; counts: number[] } {
    const lengths = new Array(288);
    for (let i = 0; i <= 143; i++) lengths[i] = 8;
    for (let i = 144; i <= 255; i++) lengths[i] = 9;
    for (let i = 256; i <= 279; i++) lengths[i] = 7;
    for (let i = 280; i <= 287; i++) lengths[i] = 8;
    return buildHuffmanTable(lengths);
  }

  function buildFixedDistTable(): { symbols: number[]; counts: number[] } {
    return buildHuffmanTable(new Array(30).fill(5));
  }

  let bfinal: number;
  do {
    bfinal = readBits(1);
    const btype = readBits(2);

    if (btype === 0) {
      bitBuf = 0;
      bitCount = 0;
      const len = data[offset] | (data[offset + 1] << 8);
      offset += 4;
      for (let i = 0; i < len; i++) output.push(data[offset++]);
    } else {
      let litLenTable: { symbols: number[]; counts: number[] };
      let distTable: { symbols: number[]; counts: number[] };

      if (btype === 1) {
        litLenTable = buildFixedLitLenTable();
        distTable = buildFixedDistTable();
      } else if (btype === 2) {
        const hlit = readBits(5) + 257;
        const hdist = readBits(5) + 1;
        const hclen = readBits(4) + 4;
        const codeLenOrder = [16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15];
        const codeLens = new Array(19).fill(0);
        for (let i = 0; i < hclen; i++) codeLens[codeLenOrder[i]] = readBits(3);
        const codeLenTable = buildHuffmanTable(codeLens);
        const lengths: number[] = [];
        while (lengths.length < hlit + hdist) {
          const sym = decodeSymbol(codeLenTable);
          if (sym < 16) {
            lengths.push(sym);
          } else if (sym === 16) {
            const r = readBits(2) + 3;
            const last = lengths[lengths.length - 1];
            for (let i = 0; i < r; i++) lengths.push(last);
          } else if (sym === 17) {
            const r = readBits(3) + 3;
            for (let i = 0; i < r; i++) lengths.push(0);
          } else if (sym === 18) {
            const r = readBits(7) + 11;
            for (let i = 0; i < r; i++) lengths.push(0);
          }
        }
        litLenTable = buildHuffmanTable(lengths.slice(0, hlit));
        distTable = buildHuffmanTable(lengths.slice(hlit));
      } else {
        throw new Error(`Invalid block type: ${btype}`);
      }

      while (true) {
        const sym = decodeSymbol(litLenTable);
        if (sym === 256) break;
        if (sym < 256) {
          output.push(sym);
        } else {
          const lenIdx = sym - 257;
          const length = lenBase[lenIdx] + (lenExtra[lenIdx] > 0 ? readBits(lenExtra[lenIdx]) : 0);
          const distSym = decodeSymbol(distTable);
          const distance =
            distBase[distSym] + (distExtra[distSym] > 0 ? readBits(distExtra[distSym]) : 0);
          for (let i = 0; i < length; i++) output.push(output[output.length - distance]);
        }
      }
    }
  } while (!bfinal);

  return new Uint8Array(output);
}

// CRC32
const crcTable = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let j = 0; j < 8; j++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[i] = c;
  }
  return table;
})();

function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i++) crc = crcTable[(crc ^ data[i]) & 0xff] ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

function writeUint32BE(data: Uint8Array, offset: number, value: number): void {
  data[offset] = (value >> 24) & 0xff;
  data[offset + 1] = (value >> 16) & 0xff;
  data[offset + 2] = (value >> 8) & 0xff;
  data[offset + 3] = value & 0xff;
}

function makeChunk(type: string, data: Uint8Array): Uint8Array {
  const chunk = new Uint8Array(4 + 4 + data.length + 4);
  writeUint32BE(chunk, 0, data.length);
  chunk[4] = type.charCodeAt(0);
  chunk[5] = type.charCodeAt(1);
  chunk[6] = type.charCodeAt(2);
  chunk[7] = type.charCodeAt(3);
  chunk.set(data, 8);
  writeUint32BE(chunk, 8 + data.length, crc32(chunk.slice(4, 8 + data.length)));
  return chunk;
}

function deflateSync(data: Uint8Array): Uint8Array {
  const header = new Uint8Array([0x78, 0x01]);
  const BLOCK_SIZE = 65535;
  const blocks: Uint8Array[] = [];
  let offset = 0;
  while (offset < data.length) {
    const end = Math.min(offset + BLOCK_SIZE, data.length);
    const chunk = data.slice(offset, end);
    const isLast = end >= data.length ? 1 : 0;
    const len = chunk.length;
    const nlen = ~len & 0xffff;
    const block = new Uint8Array(5 + len);
    block[0] = isLast;
    block[1] = len & 0xff;
    block[2] = (len >> 8) & 0xff;
    block[3] = nlen & 0xff;
    block[4] = (nlen >> 8) & 0xff;
    block.set(chunk, 5);
    blocks.push(block);
    offset = end;
  }
  let adler = 1,
    s1 = 1,
    s2 = 0;
  for (let i = 0; i < data.length; i++) {
    s1 = (s1 + data[i]) % 65521;
    s2 = (s2 + s1) % 65521;
  }
  adler = (s2 << 16) | s1;
  const trailer = new Uint8Array(4);
  writeUint32BE(trailer, 0, adler);
  const totalLen = header.length + blocks.reduce((s, b) => s + b.length, 0) + trailer.length;
  const result = new Uint8Array(totalLen);
  let off = 0;
  result.set(header, off);
  off += header.length;
  for (const b of blocks) {
    result.set(b, off);
    off += b.length;
  }
  result.set(trailer, off);
  return result;
}

/** Decode PNG bytes to raw RGBA pixel data (row-major, 4 bytes per pixel). */
export function decodePng(pngBytes: Uint8Array): {
  width: number;
  height: number;
  pixels: Uint8Array;
} {
  const chunks = readPngChunks(pngBytes);
  const ihdr = chunks.find((c) => c.type === 'IHDR')!;
  const width = readUint32BE(ihdr.data, 0);
  const height = readUint32BE(ihdr.data, 4);
  const bitDepth = ihdr.data[8];
  const colorType = ihdr.data[9];

  if (bitDepth !== 8) throw new Error(`Unsupported PNG bit depth: ${bitDepth}`);
  const hasAlpha = colorType === 6;
  const isRGB = colorType === 2;
  if (!hasAlpha && !isRGB) throw new Error(`Unsupported PNG color type: ${colorType}`);
  const channels = hasAlpha ? 4 : 3;

  const idatChunks = chunks.filter((c) => c.type === 'IDAT');
  const compressedLen = idatChunks.reduce((sum, c) => sum + c.data.length, 0);
  const compressed = new Uint8Array(compressedLen);
  let pos = 0;
  for (const chunk of idatChunks) {
    compressed.set(chunk.data, pos);
    pos += chunk.data.length;
  }

  const decompressed = inflateSync(compressed);
  const stride = width * channels + 1;
  const pixels = new Uint8Array(width * height * 4);

  for (let y = 0; y < height; y++) {
    const filterType = decompressed[y * stride];
    const rowStart = y * stride + 1;
    const pixelRow = y * width * 4;

    for (let x = 0; x < width; x++) {
      const srcIdx = rowStart + x * channels;
      let r = decompressed[srcIdx];
      let g = decompressed[srcIdx + 1];
      let b = decompressed[srcIdx + 2];
      let a = hasAlpha ? decompressed[srcIdx + 3] : 255;

      if (filterType === 1) {
        // Sub
        if (x > 0) {
          r = (r + decompressed[srcIdx - channels]) & 0xff;
          g = (g + decompressed[srcIdx - channels + 1]) & 0xff;
          b = (b + decompressed[srcIdx - channels + 2]) & 0xff;
          if (hasAlpha) a = (a + decompressed[srcIdx - channels + 3]) & 0xff;
        }
      } else if (filterType === 2) {
        // Up
        if (y > 0) {
          r = (r + decompressed[srcIdx - stride]) & 0xff;
          g = (g + decompressed[srcIdx - stride + 1]) & 0xff;
          b = (b + decompressed[srcIdx - stride + 2]) & 0xff;
          if (hasAlpha) a = (a + decompressed[srcIdx - stride + 3]) & 0xff;
        }
      } else if (filterType === 3) {
        // Average
        const left = x > 0 ? decompressed[srcIdx - channels] : 0;
        const up = y > 0 ? decompressed[srcIdx - stride] : 0;
        r = (r + Math.floor((left + up) / 2)) & 0xff;
        const leftG = x > 0 ? decompressed[srcIdx - channels + 1] : 0;
        const upG = y > 0 ? decompressed[srcIdx - stride + 1] : 0;
        g = (g + Math.floor((leftG + upG) / 2)) & 0xff;
        const leftB = x > 0 ? decompressed[srcIdx - channels + 2] : 0;
        const upB = y > 0 ? decompressed[srcIdx - stride + 2] : 0;
        b = (b + Math.floor((leftB + upB) / 2)) & 0xff;
        if (hasAlpha) {
          const leftA = x > 0 ? decompressed[srcIdx - channels + 3] : 0;
          const upA = y > 0 ? decompressed[srcIdx - stride + 3] : 0;
          a = (a + Math.floor((leftA + upA) / 2)) & 0xff;
        }
      } else if (filterType === 4) {
        // Paeth
        const leftIdx = x > 0 ? srcIdx - channels : -1;
        const upIdx = y > 0 ? srcIdx - stride : -1;
        const upLeftIdx = x > 0 && y > 0 ? srcIdx - stride - channels : -1;
        for (let c = 0; c < channels; c++) {
          const raw = decompressed[srcIdx + c];
          const left = leftIdx >= 0 ? decompressed[leftIdx + c] : 0;
          const up = upIdx >= 0 ? decompressed[upIdx + c] : 0;
          const upLeft = upLeftIdx >= 0 ? decompressed[upLeftIdx + c] : 0;
          decompressed[srcIdx + c] = (raw + paethPredictor(left, up, upLeft)) & 0xff;
        }
        r = decompressed[srcIdx];
        g = decompressed[srcIdx + 1];
        b = decompressed[srcIdx + 2];
        a = hasAlpha ? decompressed[srcIdx + 3] : 255;
      }

      if (filterType >= 1 && filterType <= 3) {
        decompressed[srcIdx] = r;
        decompressed[srcIdx + 1] = g;
        decompressed[srcIdx + 2] = b;
        if (hasAlpha) decompressed[srcIdx + 3] = a;
      }

      const dstIdx = pixelRow + x * 4;
      pixels[dstIdx] = r;
      pixels[dstIdx + 1] = g;
      pixels[dstIdx + 2] = b;
      pixels[dstIdx + 3] = a;
    }
  }

  return { width, height, pixels };
}

/** Encode raw RGBA pixels (row-major, 4 bytes per pixel) into a PNG file. */
export function encodePng(width: number, height: number, pixels: Uint8Array): Uint8Array {
  const rawData: number[] = [];
  for (let y = 0; y < height; y++) {
    rawData.push(0); // filter type: None
    const rowOffset = y * width * 4;
    for (let x = 0; x < width * 4; x++) rawData.push(pixels[rowOffset + x]);
  }

  const compressed = deflateSync(new Uint8Array(rawData));
  const chunks: Uint8Array[] = [];
  chunks.push(new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10])); // PNG signature

  const ihdr = new Uint8Array(13);
  writeUint32BE(ihdr, 0, width);
  writeUint32BE(ihdr, 4, height);
  ihdr[8] = 8;
  ihdr[9] = 6; // bit depth=8, color type=RGBA
  chunks.push(makeChunk('IHDR', ihdr));
  chunks.push(makeChunk('IDAT', compressed));
  chunks.push(makeChunk('IEND', new Uint8Array(0)));

  const totalLen = chunks.reduce((sum, c) => sum + c.length, 0);
  const result = new Uint8Array(totalLen);
  let off = 0;
  for (const chunk of chunks) {
    result.set(chunk, off);
    off += chunk.length;
  }
  return result;
}

/** Returns true if the bytes start with the PNG magic signature. */
export function isPng(bytes: Uint8Array): boolean {
  return (
    bytes.length >= 8 &&
    bytes[0] === 137 &&
    bytes[1] === 80 &&
    bytes[2] === 78 &&
    bytes[3] === 71 &&
    bytes[4] === 13 &&
    bytes[5] === 10 &&
    bytes[6] === 26 &&
    bytes[7] === 10
  );
}
